/**
 * resizeMode — the "Boundaries" reveal, as selection-scoped state.
 *
 * The grid's shared-edge splitters are transparent until hovered (and touch
 * has no hover), so the only verb that adjusts two widgets at once is
 * invisible to the people who need it most. Revealing the strips is NOT a
 * mode in the harmful sense — items keep every gesture they had; the strips
 * simply become visible, larger targets — so the state can safely be sticky
 * for as long as the selection it was opened from lasts.
 *
 * Scope: the reveal names ONE grid (the selected widget's grid, or the
 * selected section's nested grid). It clears itself whenever the selection
 * leaves that grid, so a reveal never lingers on a grid nobody is looking at.
 * A module singleton, like `altHeld`, because the bar sets it and the grids
 * read it from different subtrees.
 */
import { useSyncExternalStore } from "react";
import { useGridStore } from "@fredericrous/grid";

let revealedGridId: string | null = null;
const listeners = new Set<() => void>();
let wired = false;

function emit(): void {
  for (const l of listeners) l();
}

function set(next: string | null): void {
  if (revealedGridId === next) return;
  revealedGridId = next;
  emit();
}

/** Does the current selection still "own" the revealed grid? */
function selectionCovers(gridId: string): boolean {
  const st = useGridStore.getState();
  const sel = st.selection;
  if (!sel) return false;
  if (sel.gridId === gridId) return true;
  // A selected section block owns its nested grid.
  if (sel.itemIds.length === 1) {
    const item = st.grids[sel.gridId]?.items[sel.itemIds[0]!];
    const nested =
      item?.nestedGridId ??
      (item?.contentProps?.["nestedGridId"] as string | undefined);
    if (nested === gridId) return true;
  }
  return false;
}

function ensureWired(): void {
  if (wired) return;
  wired = true;
  useGridStore.subscribe((st, prev) => {
    if (st.selection === prev.selection) return;
    if (revealedGridId && !selectionCovers(revealedGridId)) set(null);
  });
}

export function revealBoundaries(gridId: string): void {
  ensureWired();
  set(gridId);
}

export function hideBoundaries(): void {
  set(null);
}

export function boundariesRevealedFor(): string | null {
  return revealedGridId;
}

function subscribe(cb: () => void): () => void {
  ensureWired();
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Which grid currently shows its boundaries, or null. */
export function useRevealedBoundaries(): string | null {
  return useSyncExternalStore(subscribe, boundariesRevealedFor, () => null);
}

/** True when `gridId` should draw its splitters. */
export function useBoundariesRevealed(gridId: string | undefined): boolean {
  const revealed = useRevealedBoundaries();
  return !!gridId && revealed === gridId;
}

/** Test seam. */
export function resetResizeMode(): void {
  revealedGridId = null;
  emit();
}
